import { useState } from "react";
import { goBack } from "@/lib/goBack";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/AppLayout";
import { ClientDetailsCard } from "@/components/ClientDetailsCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, User, MapPin, FileText, Briefcase, Plus, Trash2, Star, Phone, Mail, Edit2, ArrowLeft, Loader2, AlertTriangle, Globe, Copy, ExternalLink, History, CalendarClock, Workflow, CheckSquare, Hammer, PencilRuler } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/constants";

function PortalAccessPanel({ clientId, contacts, client }) {
  const [showAll, setShowAll] = useState(false);
  const { data } = useQuery({ queryKey: ["portal-visits", clientId], queryFn: async () => (await api.get(`/clients/${clientId}/portal-visits`)).data });
  const total = data?.total ?? 0;
  const visits = data?.visits || [];
  const shown = showAll ? visits : visits.slice(0, 5);
  const welcomeSentAt = client?.welcome_email_resent_at || client?.welcome_email_sent_at;
  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="portal-access-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="stamp-label text-slate-500">Portal visits</span>
          <Badge variant="outline" className={`rounded-sm text-[10px] font-mono ${total > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 text-slate-500"}`} data-testid="portal-visit-total">{total}</Badge>
          {total === 0 && <span className="text-[11px] text-slate-400">— the client hasn't opened their portal yet</span>}
        </div>
        <ResendWelcomeButton clientId={clientId} contacts={contacts} />
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="welcome-email-status">
        {welcomeSentAt ? (
          <>
            <Badge variant="outline" className="rounded-sm text-[10px] border-emerald-300 bg-emerald-50 text-emerald-800">Welcome email sent</Badge>
            <span className="text-[11px] text-slate-500">to {client?.welcome_email_to || "client"} · {fmtDateTime(welcomeSentAt)}</span>
          </>
        ) : (
          <>
            <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 bg-amber-50 text-amber-800">Welcome email not sent</Badge>
            <span className="text-[11px] text-slate-400">— their portal link hasn't been emailed yet</span>
          </>
        )}
      </div>
      {shown.length > 0 && (
        <div className="mt-2 divide-y divide-slate-100 border border-slate-100 rounded-sm" data-testid="portal-visit-log">
          {shown.map((v, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
              <span className="font-medium text-slate-700">{v.name}</span>
              <span className="text-slate-400 font-mono">{fmtDateTime(v.visited_at)}</span>
            </div>
          ))}
          {visits.length > 5 && (
            <button type="button" className="w-full text-center text-[11px] text-cyan-700 font-semibold py-1.5 hover:bg-cyan-50"
              onClick={() => setShowAll((s) => !s)} data-testid="portal-visit-toggle">
              {showAll ? "Show fewer" : `Show all ${visits.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ResendWelcomeButton({ clientId, contacts }) {
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState("");
  const [templateId, setTemplateId] = useState("default");
  const qc = useQueryClient();
  const pc = contacts.find((x) => x.is_primary && x.email) || contacts.find((x) => x.email);
  const { data: templates = [] } = useQuery({
    queryKey: ["templates"], queryFn: async () => (await api.get("/email/templates")).data, enabled: open,
  });
  const send = useMutation({
    mutationFn: async () => (await api.post(`/clients/${clientId}/send-welcome`,
      { to_email: toEmail.trim(), template_id: templateId === "default" ? null : templateId })).data,
    onSuccess: (d) => {
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["client", clientId] });
      if (d.status === "sent") toast.success(`Welcome email sent to ${d.to}`);
      else if (d.status === "mocked") toast.info("Email mocked — sending isn't configured in this environment");
      else toast.error(d.error || "Send failed — check Email → Activity");
    },
    onError: (e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not send"),
  });
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50"
        onClick={() => { setToEmail(pc?.email || ""); setTemplateId("default"); setOpen(true); }} data-testid="resend-welcome-btn">
        <Mail size={12} className="mr-1.5" /> Resend welcome email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="resend-welcome-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Resend welcome email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">To</Label>
              <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client@email.com"
                className="h-9 rounded-sm mt-1" data-testid="resend-welcome-email-input" />
            </div>
            <div>
              <Label className="text-xs">Email template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="resend-welcome-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Standard welcome (with portal link)</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 mt-1">
                Templates can use {"{{name}}"}, {"{{company}}"} and {"{{portal_url}}"} — the portal button is added automatically when a template doesn't include it.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)} data-testid="resend-welcome-cancel-btn">Cancel</Button>
            <Button className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" disabled={!toEmail.trim() || send.isPending}
              onClick={() => send.mutate()} data-testid="resend-welcome-send-btn">
              {send.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Mail size={13} className="mr-1.5" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: Building2 },
  { key: "contacts", label: "Contacts", icon: User },
  { key: "addresses", label: "Addresses", icon: MapPin },
  { key: "quotes", label: "Quotes", icon: FileText },
  { key: "jobs", label: "Jobs", icon: Briefcase },
];

const TYPE_STYLES = {
  residential: "border-emerald-300 bg-emerald-50 text-emerald-800",
  commercial: "border-sky-300 bg-sky-50 text-sky-800",
  government: "border-violet-300 bg-violet-50 text-violet-800",
  rural: "border-amber-300 bg-amber-50 text-amber-800",
  other: "border-slate-300 bg-slate-50 text-slate-700",
};

const CONTACT_METHODS = [
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

function blank() {
  return { first_name: "", last_name: "", email: "", mobile_phone: "", office_phone: "", role: "", is_primary: false, preferred_contact_method: "email" };
}
function blankAddr() {
  return { label: "Site", line1: "", line2: "", suburb: "", state: "NSW", postcode: "", country: "Australia" };
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "overview");
  const [contactModal, setContactModal] = useState(null);
  const [addressModal, setAddressModal] = useState(null);
  const [delOpen, setDelOpen] = useState(false);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => (await api.get(`/clients/${id}`)).data,
  });

  const saveContact = useMutation({
    mutationFn: async (contact) => {
      const body = { ...contact, email: (contact.email || "").trim() || null };
      if (body.id) return (await api.patch(`/clients/${id}/contacts/${body.id}`, body)).data;
      return (await api.post(`/clients/${id}/contacts`, body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); setContactModal(null); toast.success("Contact saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const deleteContact = useMutation({
    mutationFn: async (contact_id) => (await api.delete(`/clients/${id}/contacts/${contact_id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Contact removed"); },
  });
  const saveAddress = useMutation({
    mutationFn: async (a) => {
      if (a.id) return (await api.patch(`/clients/${id}/addresses/${a.id}`, a)).data;
      return (await api.post(`/clients/${id}/addresses`, a)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); setAddressModal(null); toast.success("Address saved"); },
  });
  const deleteAddress = useMutation({
    mutationFn: async (aid) => (await api.delete(`/clients/${id}/addresses/${aid}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client", id] }); toast.success("Address removed"); },
  });
  const deleteClient = useMutation({
    mutationFn: async () => (await api.delete(`/clients/${id}?cascade=true`)).data,
    onSuccess: () => {
      toast.success("Client and all linked records deleted");
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      navigate("/clients");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  if (isError) return (
    <div className="p-8 text-center">
      <div className="text-slate-700 font-medium">Client not found</div>
      <p className="text-sm text-slate-400 mt-1">This client may have been deleted.</p>
      <Button variant="outline" className="rounded-sm mt-4" onClick={() => navigate("/clients")} data-testid="client-notfound-back-btn">
        <ArrowLeft size={14} className="mr-1" /> Back to clients
      </Button>
    </div>
  );
  if (isLoading || !c) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const contacts = c.contacts || [];
  const addresses = c.addresses || [];
  const quotes = c.quotes || [];
  const jobs = c.jobs || [];

  return (
    <div>
      <PageHeader
        title={c.name}
        subtitle={c.customer_number ? `${c.customer_number}${c.abn_acn ? ` · ABN ${c.abn_acn}` : ""}` : (c.abn_acn ? `ABN ${c.abn_acn}` : "Client record")}
        breadcrumb={["Clients", c.name]}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => goBack(navigate, "/clients")} className="rounded-sm h-10"><ArrowLeft size={14} className="mr-1.5" />Back</Button>
            <Button data-testid="delete-client-btn" variant="outline" onClick={() => setDelOpen(true)} className="rounded-sm h-10 border-rose-300 text-rose-600 hover:bg-rose-50">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </Button>
            <Button data-testid="new-deal-from-client" onClick={async () => {
              try {
                const contacts = (await api.get("/contacts", { params: { company_id: id } })).data;
                const pc = contacts.find((x) => x.is_primary) || contacts[0];
                const { data: d } = await api.post("/deals", {
                  title: `${c.name} — new enquiry`, company_id: id, contact_id: pc?.id || null,
                  value: 0, stage: "enquiry", fence_type: "other",
                });
                toast.success(`Deal ${d.job_number || ""} created`);
                navigate(`/deals?open=${d.id}`);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Could not create deal");
              }
            }} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-10">
              <Plus size={14} className="mr-1.5" /> New Enquiry / Deal
            </Button>
          </div>
        }
      />

      <div className="px-6 lg:px-10 py-6 space-y-4">
        {/* Summary strip */}
        <Card className="rounded-sm p-4 industrial-shadow">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="stamp-label text-slate-500">Client Type</div>
              <div className="mt-1">
                {c.client_type ? <Badge variant="outline" className={`rounded-sm ${TYPE_STYLES[c.client_type] || TYPE_STYLES.other}`}>{c.client_type}</Badge> : <span className="text-slate-400 text-xs">—</span>}
              </div>
            </div>
            <div>
              <div className="stamp-label text-slate-500">Contacts</div>
              <div className="font-mono font-bold text-xl">{contacts.length}</div>
            </div>
            <div>
              <div className="stamp-label text-slate-500">Addresses</div>
              <div className="font-mono font-bold text-xl">{addresses.length}</div>
            </div>
            <div>
              <div className="stamp-label text-slate-500">Quotes</div>
              <div className="font-mono font-bold text-xl">{quotes.length}</div>
            </div>
            <div>
              <div className="stamp-label text-slate-500">Jobs</div>
              <div className="font-mono font-bold text-xl">{jobs.length}</div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`client-tab-${t.key}`} onClick={() => setTab(t.key)}
                      className={`px-4 h-10 flex items-center gap-2 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${active ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ClientDetailsCard client={c} />
            <Card className="rounded-sm p-4 industrial-shadow">
              <div className="stamp-label text-slate-500 mb-2">Primary Contact</div>
              {(() => {
                const pc = contacts.find((x) => x.is_primary) || contacts[0];
                if (!pc) return <div className="text-slate-400 text-sm">No contacts yet.</div>;
                return (
                  <div className="space-y-1.5 text-sm">
                    <div className="font-semibold text-slate-900">{pc.first_name} {pc.last_name}</div>
                    {pc.role && <div className="text-xs text-slate-500">{pc.role}</div>}
                    {pc.mobile_phone && <div className="flex items-center gap-1.5 text-xs text-slate-700"><Phone size={11} className="text-slate-400" /> <span className="font-mono">{pc.mobile_phone}</span> <span className="text-slate-400">(mobile)</span></div>}
                    {pc.office_phone && <div className="flex items-center gap-1.5 text-xs text-slate-700"><Phone size={11} className="text-slate-400" /> <span className="font-mono">{pc.office_phone}</span> <span className="text-slate-400">(office)</span></div>}
                    {pc.email && <div className="flex items-center gap-1.5 text-xs text-slate-700"><Mail size={11} className="text-slate-400" /> {pc.email}</div>}
                    {pc.preferred_contact_method && <div className="text-[10px] text-slate-500 uppercase font-semibold mt-2">Prefers {pc.preferred_contact_method}</div>}
                  </div>
                );
              })()}
              <div className="mt-3 pt-3 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => { setTab("contacts"); }} className="rounded-sm h-8 text-xs">
                  <Edit2 size={11} className="mr-1.5" /> Manage contacts
                </Button>
              </div>
            </Card>

            {/* Client portal access */}
            <Card className="rounded-sm p-4 industrial-shadow md:col-span-2" data-testid="client-portal-access-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="stamp-label text-cyan-600 flex items-center gap-1.5"><Globe size={13} /> Client Portal Access</div>
                  <p className="text-xs text-slate-500 mt-1">
                    Their personal portal — quotes to review &amp; accept online, live job progress and bank transfer details.
                    The same link is sent automatically in their welcome email.
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm" variant="outline" data-testid="copy-portal-link-btn"
                    onClick={async () => {
                      let url;
                      try {
                        url = (await api.get(`/clients/${id}/portal-link`)).data.url;
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Could not get portal link");
                        return;
                      }
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success("Portal link copied — text or email it to the client");
                      } catch {
                        toast.info(`Clipboard blocked — link: ${url}`, { duration: 15000 });
                      }
                    }}
                    className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  >
                    <Copy size={13} className="mr-1.5" /> Copy portal link
                  </Button>
                  <Button
                    size="sm" variant="outline" data-testid="open-portal-link-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.get(`/clients/${id}/portal-link`);
                        window.open(`${data.url}${data.url.includes("?") ? "&" : "?"}preview=1`, "_blank", "noopener");
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Could not get portal link");
                      }
                    }}
                    className="rounded-sm h-9"
                  >
                    <ExternalLink size={13} className="mr-1.5" /> Preview
                  </Button>
                </div>
              </div>
              <PortalAccessPanel clientId={id} contacts={contacts} client={c} />
            </Card>
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button data-testid="add-contact-detail-btn" onClick={() => setContactModal(blank())} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-9"><Plus size={14} className="mr-1.5" /> Add contact</Button>
            </div>
            {contacts.map((ct) => (
              <Card key={ct.id} className="rounded-sm p-4 industrial-shadow" data-testid={`contact-row-${ct.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{ct.first_name} {ct.last_name}</span>
                      {ct.is_primary && <Badge variant="outline" className="rounded-sm text-[9px] border-cyan-300 bg-cyan-50 text-cyan-800"><Star size={9} className="mr-0.5" />Primary</Badge>}
                      {ct.role && <Badge variant="outline" className="rounded-sm text-[10px]">{ct.role}</Badge>}
                    </div>
                    <div className="text-xs text-slate-600 mt-2 space-y-1">
                      {ct.mobile_phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-slate-400" /> <span className="font-mono">{ct.mobile_phone}</span> <span className="text-slate-400">mobile</span></div>}
                      {ct.office_phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-slate-400" /> <span className="font-mono">{ct.office_phone}</span> <span className="text-slate-400">office</span></div>}
                      {ct.email && <div className="flex items-center gap-1.5"><Mail size={11} className="text-slate-400" /> {ct.email}</div>}
                      {ct.preferred_contact_method && <div className="text-[10px] text-slate-500 stamp-label mt-1">Prefers {ct.preferred_contact_method}</div>}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setContactModal(ct)} className="rounded-sm h-8 w-8 p-0" data-testid={`edit-contact-${ct.id}`}><Edit2 size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => window.confirm(`Remove ${ct.first_name}?`) && deleteContact.mutate(ct.id)} className="rounded-sm h-8 w-8 p-0 text-rose-600" data-testid={`delete-contact-${ct.id}`}><Trash2 size={12} /></Button>
                  </div>
                </div>
              </Card>
            ))}
            {contacts.length === 0 && <Card className="rounded-sm p-8 text-center text-sm text-slate-400">No contacts yet.</Card>}
          </div>
        )}

        {tab === "addresses" && (
          <div className="space-y-3">
            <div className="flex justify-end">
              <Button data-testid="add-address-detail-btn" onClick={() => setAddressModal(blankAddr())} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-9"><Plus size={14} className="mr-1.5" /> Add address</Button>
            </div>
            {addresses.map((a) => (
              <Card key={a.id} className="rounded-sm p-4 industrial-shadow" data-testid={`address-row-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900 flex items-center gap-2"><MapPin size={13} className="text-cyan-600" /> {a.label}</div>
                    <div className="text-xs text-slate-600 mt-1.5">
                      {a.line1}{a.line2 ? `, ${a.line2}` : ""}<br />
                      {a.suburb} {a.state} {a.postcode}, {a.country}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => setAddressModal(a)} className="rounded-sm h-8 w-8 p-0" data-testid={`edit-address-${a.id}`}><Edit2 size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => window.confirm(`Remove ${a.label}?`) && deleteAddress.mutate(a.id)} className="rounded-sm h-8 w-8 p-0 text-rose-600" data-testid={`delete-address-${a.id}`}><Trash2 size={12} /></Button>
                  </div>
                </div>
              </Card>
            ))}
            {addresses.length === 0 && <Card className="rounded-sm p-8 text-center text-sm text-slate-400">No addresses on file.</Card>}
          </div>
        )}

        {tab === "quotes" && (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button data-testid="new-quote-tab-btn" onClick={() => navigate(`/quotes/new?client_id=${id}`)} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm h-9"><Plus size={14} className="mr-1.5" /> New Quote</Button>
            </div>
            {quotes.map((q) => (
              <Card key={q.id} className="rounded-sm p-3 industrial-shadow cursor-pointer hover:border-cyan-400" onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`quote-row-${q.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono font-semibold text-slate-900">{q.quote_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{q.customer_name} · {q.created_at?.slice(0, 10)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="rounded-sm text-[10px]">{q.status || "draft"}</Badge>
                    <div className="font-mono font-bold text-slate-900">${(q.sale_price || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}</div>
                  </div>
                </div>
              </Card>
            ))}
            {quotes.length === 0 && <Card className="rounded-sm p-8 text-center text-sm text-slate-400">No quotes yet — create one to get started.</Card>}
          </div>
        )}

        {tab === "jobs" && (
          <div className="space-y-2">
            {jobs.map((j) => (
              <Card key={j.id} className="rounded-sm p-3 industrial-shadow cursor-pointer hover:border-cyan-400" onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-row-${j.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono font-semibold text-slate-900">{j.job_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{j.title} · {j.scheduled_start?.slice(0, 10) || "unscheduled"}</div>
                  </div>
                  <Badge variant="outline" className="rounded-sm text-[10px]">{j.status || "scheduled"}</Badge>
                </div>
              </Card>
            ))}
            {jobs.length === 0 && <Card className="rounded-sm p-8 text-center text-sm text-slate-400">No jobs yet.</Card>}
          </div>
        )}

      </div>

      {/* Delete client confirm */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="delete-client-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><AlertTriangle size={18} className="text-rose-500" /> Delete {c.name}?</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>This permanently deletes the client and <b>everything linked to them</b>:</p>
            <div className="border border-rose-200 bg-rose-50 rounded-sm p-3 text-xs space-y-1">
              <div>• {contacts.length} contact(s) &amp; {addresses.length} address(es)</div>
              <div>• {quotes.length} quote(s) — including attachments</div>
              <div>• {jobs.length} job(s), plus any deals, bookings &amp; tasks</div>
            </div>
            <p className="text-xs text-slate-400">This cannot be undone.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="confirm-delete-client-btn" onClick={() => deleteClient.mutate()} disabled={deleteClient.isPending} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              {deleteClient.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
              Delete everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact modal */}
      <Dialog open={!!contactModal} onOpenChange={(o) => !o && setContactModal(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{contactModal?.id ? "Edit contact" : "Add contact"}</DialogTitle></DialogHeader>
          {contactModal && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First name *</Label><Input value={contactModal.first_name} onChange={(e) => setContactModal({ ...contactModal, first_name: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label>Last name *</Label><Input value={contactModal.last_name} onChange={(e) => setContactModal({ ...contactModal, last_name: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div className="col-span-2"><Label>Role</Label><Input value={contactModal.role || ""} onChange={(e) => setContactModal({ ...contactModal, role: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div className="col-span-2"><Label>Email</Label><Input type="email" value={contactModal.email || ""} onChange={(e) => setContactModal({ ...contactModal, email: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label>Mobile</Label><Input value={contactModal.mobile_phone || ""} onChange={(e) => setContactModal({ ...contactModal, mobile_phone: e.target.value })} className="rounded-sm h-9 mt-1 font-mono" /></div>
              <div><Label>Office</Label><Input value={contactModal.office_phone || ""} onChange={(e) => setContactModal({ ...contactModal, office_phone: e.target.value })} className="rounded-sm h-9 mt-1 font-mono" /></div>
              <div className="col-span-2">
                <Label>Preferred contact method</Label>
                <Select value={contactModal.preferred_contact_method || "email"} onValueChange={(v) => setContactModal({ ...contactModal, preferred_contact_method: v })}>
                  <SelectTrigger className="rounded-sm h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTACT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2 pt-2">
                <input type="checkbox" checked={!!contactModal.is_primary} onChange={(e) => setContactModal({ ...contactModal, is_primary: e.target.checked })} id="isprim" />
                <label htmlFor="isprim" className="text-sm">Set as primary contact</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactModal(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="contact-save-btn" onClick={() => saveContact.mutate(contactModal)} disabled={!contactModal?.first_name || !contactModal?.last_name} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Address modal */}
      <Dialog open={!!addressModal} onOpenChange={(o) => !o && setAddressModal(null)}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle>{addressModal?.id ? "Edit address" : "Add address"}</DialogTitle></DialogHeader>
          {addressModal && (
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Label>Label</Label><Input value={addressModal.label} onChange={(e) => setAddressModal({ ...addressModal, label: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div className="col-span-2"><Label>Street *</Label><Input value={addressModal.line1} onChange={(e) => setAddressModal({ ...addressModal, line1: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div className="col-span-2"><Label>Line 2</Label><Input value={addressModal.line2 || ""} onChange={(e) => setAddressModal({ ...addressModal, line2: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label>Suburb *</Label><Input value={addressModal.suburb} onChange={(e) => setAddressModal({ ...addressModal, suburb: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label>State</Label><Input value={addressModal.state} onChange={(e) => setAddressModal({ ...addressModal, state: e.target.value })} className="rounded-sm h-9 mt-1 font-mono uppercase" /></div>
              <div><Label>Postcode *</Label><Input value={addressModal.postcode} onChange={(e) => setAddressModal({ ...addressModal, postcode: e.target.value })} className="rounded-sm h-9 mt-1 font-mono" /></div>
              <div><Label>Country</Label><Input value={addressModal.country} onChange={(e) => setAddressModal({ ...addressModal, country: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressModal(null)} className="rounded-sm">Cancel</Button>
            <Button data-testid="address-save-btn" onClick={() => saveAddress.mutate(addressModal)} disabled={!addressModal?.line1 || !addressModal?.suburb || !addressModal?.postcode} className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

